import { useEffect, useState } from "react";
import { listOnsiteCities } from "@/api/locations";
import { logger, serializeError } from "@/lib/logger";

export interface OnsiteCity {
  city: string;
  country: string;
}

// Module-level cache so the landing form and profile don't each re-query the
// (small, rarely-changing) list on every mount.
let cache: OnsiteCity[] | null = null;

/**
 * Distinct cities that actually host onsite events. Reads v_onsite_city — a
 * DB-side distinct of non-virtual locations with >=1 event (anon-readable) — so
 * the client makes one small query instead of pulling every v_event row.
 * Drives the LocationPicker city list.
 */
export function useOnsiteLocations(): { cities: OnsiteCity[]; loading: boolean } {
  const [cities, setCities] = useState<OnsiteCity[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    if (cache !== null) return;
    let cancelled = false;

    (async () => {
      const { data, error } = await listOnsiteCities();
      if (cancelled) return;
      if (error || !data) {
        // A missing v_onsite_city view (un-applied migration) or perms error
        // looks identical to "no cities" without this — log so it's debuggable.
        if (error) logger.error("useOnsiteLocations: failed to load v_onsite_city", serializeError(error));
        setLoading(false);
        return;
      }
      const onsite = data
        .filter((r): r is OnsiteCity => Boolean(r.city && r.country))
        .map((r) => ({ city: r.city, country: r.country }));
      cache = onsite;
      setCities(onsite);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { cities, loading };
}
